import React from "react";
import { useTranslation } from "react-i18next";
import { Building2, Package, Users, Target, MessageSquareText, Palette, Share2, Flag, Handshake, BookOpen, FileUp } from "lucide-react";
import { Field, TextField, TextareaField, LinesField, CsvField } from "@/components/common/FormField";
import { RepeaterField } from "@/components/common/RepeaterField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { COUNTRIES, CURRENCIES, TIMEZONES, CONTENT_LANGUAGES, GOAL_KEYS, countryName, languageName } from "@/lib/options";

export const SECTION_DEFS = [
  { key: "identity", icon: Building2 },
  { key: "offer", icon: Package },
  { key: "audience", icon: Users },
  { key: "positioning", icon: Target },
  { key: "voice", icon: MessageSquareText },
  { key: "visual", icon: Palette },
  { key: "social", icon: Share2 },
  { key: "goals", icon: Flag },
  { key: "sales", icon: Handshake },
  { key: "knowledge", icon: BookOpen },
];

// Which section holds each required-for-AI field (used to jump from the readiness card).
export const FIELD_SECTION = {
  company_name: "identity", industry: "identity", description: "identity", target_audience: "audience", products_services: "offer",
  tone: "voice", geo_zones: "audience", goals: "goals", offers: "offer", primary_goal: "goals",
};

const SelectField = ({ id, label, value, onChange, options, essential, optional, placeholder, testId }) => {
  const { t } = useTranslation();
  return (
    <Field id={id} label={label} essential={essential} optional={optional}>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={testId || id} className="bg-card"><SelectValue placeholder={placeholder || t("common.select")} /></SelectTrigger>
        <SelectContent className="bg-card">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
};

/** Renders the fields of one Brand Brain section. `draft` is the merged server+edits object. */
export const SectionForm = ({ section, draft, setField, setNested }) => {
  const { t, i18n } = useTranslation();
  const nested = (key) => draft[key] || {};
  const lang = i18n.language;

  switch (section) {
    case "identity":
      return (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField id="bb-company-name" label={t("brandBrain.companyName")} essential value={draft.company_name} onChange={(v) => setField("company_name", v)} />
            <TextField id="bb-brand-name" label={t("brandBrain.brandName")} optional value={draft.brand_name} onChange={(v) => setField("brand_name", v)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField id="bb-website" label={t("brandBrain.website")} optional type="url" placeholder="https://" value={draft.website} onChange={(v) => setField("website", v)} />
            <TextField id="bb-industry" label={t("brandBrain.industry")} essential value={draft.industry} onChange={(v) => setField("industry", v)} />
          </div>
          <TextField id="bb-short-description" label={t("brandBrain.shortDescription")} hint={t("brandBrain.shortDescriptionHint")} value={draft.short_description} onChange={(v) => setField("short_description", v)} maxLength={400} />
          <TextareaField id="bb-description" label={t("brandBrain.description")} essential rows={5} value={draft.description} onChange={(v) => setField("description", v)} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SelectField id="bb-country" label={t("brandBrain.country")} value={nested("location").country} onChange={(v) => setNested("location", "country", v)} options={COUNTRIES.map((c) => ({ value: c, label: countryName(c, lang) }))} />
            <TextField id="bb-city" label={t("brandBrain.city")} value={nested("location").city} onChange={(v) => setNested("location", "city", v)} />
            <SelectField id="bb-timezone" label={t("brandBrain.timezone")} value={nested("location").timezone} onChange={(v) => setNested("location", "timezone", v)} options={TIMEZONES.map((z) => ({ value: z, label: z }))} />
            <SelectField id="bb-currency" label={t("brandBrain.currency")} value={nested("location").currency} onChange={(v) => setNested("location", "currency", v)} options={CURRENCIES.map((c) => ({ value: c, label: c }))} />
          </div>
          <CsvField id="bb-languages" label={t("brandBrain.languages")} hint={`${t("common.commaSeparated")} — ${CONTENT_LANGUAGES.map((c) => `${c} (${languageName(c, lang)})`).join(", ")}`} value={draft.content_languages} onChange={(v) => setField("content_languages", v.map((s) => s.toLowerCase()))} />
        </div>
      );

    case "offer":
      return (
        <div className="space-y-6">
          <RepeaterField id="bb-services" label={t("brandBrain.services")} rows={draft.products_services} onChange={(v) => setField("products_services", v)} addLabel={t("brandBrain.addService")} emptyLabel={t("brandBrain.empty.services")}
            columns={[{ key: "name", label: t("brandBrain.itemName"), span: "sm:col-span-3" }, { key: "price", label: t("brandBrain.itemPrice"), span: "sm:col-span-3" }, { key: "description", label: t("brandBrain.itemDescription"), type: "textarea" }]} />
          <RepeaterField id="bb-offers" label={t("brandBrain.offers")} rows={draft.offers} onChange={(v) => setField("offers", v)} addLabel={t("brandBrain.addOffer")} emptyLabel={t("brandBrain.empty.offers")}
            columns={[
              { key: "name", label: t("brandBrain.itemName"), span: "sm:col-span-2" },
              { key: "price", label: t("brandBrain.itemPrice"), span: "sm:col-span-2" },
              { key: "kind", label: t("brandBrain.offerKind"), type: "select", span: "sm:col-span-2", options: ["main", "priority", "seasonal"].map((k) => ({ value: k, label: t(`brandBrain.offerKinds.${k}`) })) },
              { key: "description", label: t("brandBrain.itemDescription"), type: "textarea", span: "sm:col-span-3" },
              { key: "conditions", label: t("brandBrain.itemConditions"), type: "textarea", span: "sm:col-span-3" },
            ]} />
          <TextareaField id="bb-usp" label={t("brandBrain.usp")} rows={3} value={draft.usp} onChange={(v) => setField("usp", v)} />
          <TextareaField id="bb-pricing-notes" label={t("brandBrain.pricingNotes")} rows={2} value={draft.pricing_notes} onChange={(v) => setField("pricing_notes", v)} />
        </div>
      );

    case "audience": {
      const a = nested("audience_details");
      return (
        <div className="space-y-5">
          <TextareaField id="bb-target" label={t("brandBrain.target")} essential rows={3} value={draft.target_audience} onChange={(v) => setField("target_audience", v)} />
          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField id="bb-segment" label={t("brandBrain.segment")} value={a.segment} onChange={(v) => setNested("audience_details", "segment", v)} options={["b2b", "b2c", "both"].map((k) => ({ value: k, label: t(`brandBrain.segments.${k}`) }))} />
            <TextField id="bb-age-range" label={t("brandBrain.ageRange")} optional value={a.age_range} onChange={(v) => setNested("audience_details", "age_range", v)} />
            <CsvField id="bb-geo" label={t("brandBrain.geo")} essential value={draft.geo_zones} onChange={(v) => setField("geo_zones", v)} />
          </div>
          <CsvField id="bb-customer-types" label={t("brandBrain.customerTypes")} value={a.customer_types} onChange={(v) => setNested("audience_details", "customer_types", v)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <LinesField id="bb-needs" label={t("brandBrain.needs")} rows={3} value={a.needs} onChange={(v) => setNested("audience_details", "needs", v)} />
            <LinesField id="bb-pains" label={t("brandBrain.pains")} rows={3} value={a.pains} onChange={(v) => setNested("audience_details", "pains", v)} />
            <LinesField id="bb-objections" label={t("brandBrain.objections")} rows={3} value={a.objections} onChange={(v) => setNested("audience_details", "objections", v)} />
            <LinesField id="bb-motivations" label={t("brandBrain.motivations")} rows={3} value={a.motivations} onChange={(v) => setNested("audience_details", "motivations", v)} />
          </div>
          <RepeaterField id="bb-personas" label={t("brandBrain.personas")} rows={draft.personas} onChange={(v) => setField("personas", v)} addLabel={t("brandBrain.addPersona")} emptyLabel={t("brandBrain.empty.personas")}
            columns={[{ key: "name", label: t("brandBrain.personaName") }, { key: "description", label: t("brandBrain.personaDescription") }, { key: "pains", label: t("brandBrain.personaPains"), type: "csv" }, { key: "goals", label: t("brandBrain.personaGoals"), type: "csv" }]} />
        </div>
      );
    }

    case "positioning": {
      const p = nested("positioning_details");
      return (
        <div className="space-y-5">
          <TextareaField id="bb-positioning" label={t("brandBrain.positioning")} rows={3} value={draft.positioning} onChange={(v) => setField("positioning", v)} />
          <TextField id="bb-promise" label={t("brandBrain.promise")} value={p.promise} onChange={(v) => setNested("positioning_details", "promise", v)} />
          <div className="grid gap-4 sm:grid-cols-3">
            <LinesField id="bb-differentiators" label={t("brandBrain.differentiators")} rows={4} value={p.differentiators} onChange={(v) => setNested("positioning_details", "differentiators", v)} />
            <LinesField id="bb-highlights" label={t("brandBrain.highlights")} rows={4} value={p.highlights} onChange={(v) => setNested("positioning_details", "highlights", v)} />
            <LinesField id="bb-avoid" label={t("brandBrain.avoidTopics")} rows={4} value={p.avoid} onChange={(v) => setNested("positioning_details", "avoid", v)} />
          </div>
          <RepeaterField id="bb-competitors" label={t("brandBrain.competitors")} rows={draft.competitors} onChange={(v) => setField("competitors", v)} addLabel={t("brandBrain.addCompetitor")} emptyLabel={t("brandBrain.empty.competitors")}
            columns={[{ key: "name", label: t("brandBrain.competitorName") }, { key: "website", label: t("brandBrain.competitorWebsite") }, { key: "notes", label: t("brandBrain.competitorNotes"), type: "textarea" }]} />
        </div>
      );
    }

    case "voice": {
      const c = nested("communication");
      return (
        <div className="space-y-5">
          <TextareaField id="bb-tone" label={t("brandBrain.tone")} essential rows={2} value={draft.tone} onChange={(v) => setField("tone", v)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <CsvField id="bb-tone-attributes" label={t("brandBrain.toneAttributes")} value={draft.tone_attributes} onChange={(v) => setField("tone_attributes", v)} />
            <SelectField id="bb-formality" label={t("brandBrain.formality")} value={c.formality} onChange={(v) => setNested("communication", "formality", v)} options={["vous", "tu", "mixed"].map((k) => ({ value: k, label: t(`brandBrain.formalities.${k}`) }))} />
          </div>
          <TextareaField id="bb-writing-style" label={t("brandBrain.writingStyle")} rows={2} value={c.writing_style} onChange={(v) => setNested("communication", "writing_style", v)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <CsvField id="bb-words-use" label={t("brandBrain.wordsToUse")} value={draft.words_to_use} onChange={(v) => setField("words_to_use", v)} />
            <CsvField id="bb-words-avoid" label={t("brandBrain.wordsToAvoid")} value={draft.words_to_avoid} onChange={(v) => setField("words_to_avoid", v)} />
          </div>
          <LinesField id="bb-ctas" label={t("brandBrain.ctas")} rows={3} value={draft.ctas} onChange={(v) => setField("ctas", v)} />
          <LinesField id="bb-examples" label={t("brandBrain.examples")} rows={4} value={c.examples} onChange={(v) => setNested("communication", "examples", v)} />
        </div>
      );
    }

    case "visual": {
      const v = nested("visual_identity");
      return (
        <div className="space-y-5">
          <Alert className="border-amber-200 bg-amber-50 text-amber-950" data-testid="bb-storage-notice">
            <FileUp className="h-4 w-4" />
            <AlertDescription>{t("brandBrain.storageSoon")}</AlertDescription>
          </Alert>
          <TextField id="bb-logo-url" label={t("brandBrain.logoUrl")} hint={t("brandBrain.logoHint")} type="url" placeholder="https://" value={v.logo_url || ""} onChange={(x) => setNested("visual_identity", "logo_url", x)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <CsvField id="bb-colors" label={t("brandBrain.colors")} placeholder="#1F2933, #2F5D3A" value={v.colors} onChange={(x) => setNested("visual_identity", "colors", x)} />
            <CsvField id="bb-secondary-colors" label={t("brandBrain.secondaryColors")} value={v.secondary_colors} onChange={(x) => setNested("visual_identity", "secondary_colors", x)} />
          </div>
          {Array.isArray(v.colors) && v.colors.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="bb-color-swatches">
              {[...v.colors, ...(v.secondary_colors || [])].map((color, i) => (
                <span key={`${color}-${i}`} className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs"><span className="h-3.5 w-3.5 rounded-sm border border-border" style={{ backgroundColor: color }} />{color}</span>
              ))}
            </div>
          )}
          <CsvField id="bb-fonts" label={t("brandBrain.fonts")} value={v.fonts} onChange={(x) => setNested("visual_identity", "fonts", x)} />
          <TextareaField id="bb-style" label={t("brandBrain.style")} rows={3} value={v.style} onChange={(x) => setNested("visual_identity", "style", x)} />
          <LinesField id="bb-references" label={t("brandBrain.references")} rows={3} value={v.references} onChange={(x) => setNested("visual_identity", "references", x)} />
        </div>
      );
    }

    case "social": {
      const s = nested("social_links");
      const nets = [["instagram", "Instagram"], ["facebook", "Facebook"], ["linkedin", "LinkedIn"], ["tiktok", t("brandBrain.tiktok")], ["google_business", t("brandBrain.googleBusiness")]];
      return (
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">{t("brandBrain.socialHint")}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {nets.map(([key, label]) => (
              <TextField key={key} id={`bb-social-${key}`} label={label} type="url" placeholder="https://" value={s[key] || ""} onChange={(v) => setNested("social_links", key, v)} />
            ))}
          </div>
          <LinesField id="bb-social-other" label={t("brandBrain.otherProfiles")} rows={3} value={Array.isArray(s.other) ? s.other : []} onChange={(v) => setNested("social_links", "other", v)} />
        </div>
      );
    }

    case "goals":
      return (
        <div className="space-y-5">
          <Field id="bb-primary-goal" label={t("brandBrain.primaryGoal")} essential>
            <div className="grid gap-3 sm:grid-cols-[220px_1fr]">
              <Select value={GOAL_KEYS.find((g) => t(`brandBrain.goalOptions.${g}`) === draft.primary_goal) || (draft.primary_goal ? "other" : "")} onValueChange={(v) => setField("primary_goal", v === "other" ? (draft.primary_goal && !GOAL_KEYS.some((g) => t(`brandBrain.goalOptions.${g}`) === draft.primary_goal) ? draft.primary_goal : t("brandBrain.goalOptions.other")) : t(`brandBrain.goalOptions.${v}`))}>
                <SelectTrigger id="bb-primary-goal" data-testid="bb-primary-goal-select" className="bg-card"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                <SelectContent className="bg-card">{GOAL_KEYS.map((g) => <SelectItem key={g} value={g}>{t(`brandBrain.goalOptions.${g}`)}</SelectItem>)}</SelectContent>
              </Select>
              <TextField id="bb-primary-goal-text" value={draft.primary_goal} onChange={(v) => setField("primary_goal", v)} testId="bb-primary-goal-input" />
            </div>
          </Field>
          <LinesField id="bb-goals" label={t("brandBrain.goalsPriority")} essential hint={t("brandBrain.goalsPriorityHint")} rows={5} value={draft.goals} onChange={(v) => setField("goals", v)} />
        </div>
      );

    case "sales": {
      const s = nested("sales_context");
      const set = (k) => (v) => setNested("sales_context", k, v);
      return (
        <div className="space-y-5">
          <TextareaField id="bb-sales-process" label={t("brandBrain.salesProcess")} rows={3} value={s.process} onChange={set("process")} />
          <TextareaField id="bb-ideal-lead" label={t("brandBrain.idealLead")} rows={2} value={s.ideal_lead} onChange={set("ideal_lead")} />
          <div className="grid gap-4 sm:grid-cols-2">
            <LinesField id="bb-good-criteria" label={t("brandBrain.goodCriteria")} rows={4} value={s.good_criteria} onChange={set("good_criteria")} />
            <LinesField id="bb-bad-criteria" label={t("brandBrain.badCriteria")} rows={4} value={s.bad_criteria} onChange={set("bad_criteria")} />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField id="bb-cycle" label={t("brandBrain.cycleLength")} value={s.cycle_length} onChange={set("cycle_length")} />
            <CsvField id="bb-conversion-channels" label={t("brandBrain.conversionChannels")} value={s.conversion_channels} onChange={set("conversion_channels")} />
            <TextField id="bb-appointment-method" label={t("brandBrain.appointmentMethod")} value={s.appointment_method} onChange={set("appointment_method")} />
          </div>
        </div>
      );
    }

    case "knowledge":
      return (
        <div className="space-y-6">
          <RepeaterField id="bb-faq" label={t("brandBrain.faq")} rows={draft.faq} onChange={(v) => setField("faq", v)} addLabel={t("brandBrain.addFaq")} emptyLabel={t("brandBrain.empty.faq")}
            columns={[{ key: "question", label: t("brandBrain.question"), span: "sm:col-span-6" }, { key: "answer", label: t("brandBrain.answer"), type: "textarea" }]} />
          <TextareaField id="bb-knowledge-notes" label={t("brandBrain.knowledgeNotes")} hint={t("brandBrain.knowledgeNotesHint")} rows={5} value={draft.knowledge_notes} onChange={(v) => setField("knowledge_notes", v)} />
        </div>
      );

    default:
      return null;
  }
};
